<template>
  <div
    class="relative flex h-screen w-full max-w-full overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />



    <div class="flex flex-col flex-1 min-w-0 min-h-0 w-full overflow-y-auto custom-scrollbar">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 min-w-0 px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div class="max-w-6xl mx-auto space-y-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 class="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">
                {{ pageTitle }}
              </h1>
              <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                {{ pageSubtitle }}
              </p>
            </div>
            <RouterLink
              to="/consultations/my"
              class="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 sm:py-2 text-xs font-medium border rounded-xl border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
            >
              <i
                class="mdi mdi-arrow-left text-[16px]"
                :class="lang === 'ar' ? 'ml-1.5 rotate-180' : 'mr-1.5'"
              ></i>
              {{ t("consult_coming_back_my") }}
            </RouterLink>
          </div>

          <section
            class="overflow-hidden bg-white border shadow-sm dark:bg-slate-900 border-slate-200/80 dark:border-slate-800 rounded-2xl"
          >
            <header
              class="px-3.5 py-3 sm:px-5 border-b border-slate-200/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/40 backdrop-blur-sm"
            >
              <div class="flex flex-col md:flex-row md:items-center gap-3 min-w-0">
                <FilterSearchInput
                  v-model="searchInput"
                  :lang="lang"
                  :placeholder="t('consult_coming_search_placeholder')"
                  :aria-label="t('consult_coming_search_hint')"
                  :clear-title="lang === 'ar' ? 'مسح' : 'Clear'"
                  @clear="clearSearch"
                  class="w-full md:w-64 lg:w-72 shrink-0"
                />

                <div class="flex-1 min-w-0 overflow-x-auto custom-scrollbar -mx-1 px-1 py-0.5">
                  <TicketFilterPills
                    class="min-w-max"
                    :pills="filterPills"
                    :active-key="activePill"
                    :aria-label="t('consult_coming_filter_label')"
                    @update:active-key="onPillChange"
                  />
                </div>
              </div>

              <div
                class="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50"
              >
                <SearchableSelect
                  v-model="extraFilters.type_id"
                  :options="typeSelectOptions"
                  :lang="lang"
                  min-width="10rem"
                  max-width="16rem"
                  menu-min-width="18rem"
                  menu-max-width="26rem"
                  :placeholder="t('consult_filter_type_all')"
                  :search-placeholder="t('consult_filter_type_search')"
                  :empty-label="t('consult_filter_type_empty')"
                />
                <!-- Payment status moved to quick-filter pills (Paid) -->
                <!--
                <CompactSelect
                  v-model="extraFilters.payment_status"
                  :options="paymentSelectOptions"
                  min-width="9rem"
                />
                -->
                <CompactSelect
                  v-model="extraFilters.sortBy"
                  :options="sortSelectOptions"
                  min-width="8.5rem"
                />
                <CompactSelect
                  v-model="extraFilters.orderBy"
                  :options="orderSelectOptions"
                  min-width="6rem"
                />
                <button
                  type="button"
                  class="inline-flex items-center gap-1 h-9 px-2.5 text-[11px] font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  @click="showAdvanced = !showAdvanced"
                >
                  <i class="mdi mdi-tune-variant text-[15px]"></i>
                  {{ t("consult_coming_filter_more") }}
                </button>
              </div>

              <div v-if="showAdvanced" class="mt-3 max-w-md">
                <DateRangePicker
                  v-model:from="extraFilters.appointment_date_from"
                  v-model:to="extraFilters.appointment_date_to"
                  :lang="lang"
                  :label="t('consult_coming_filter_appointment_range')"
                  :from-label="t('consult_coming_filter_appointment_from')"
                  :to-label="t('consult_coming_filter_appointment_to')"
                  :placeholder="t('consult_coming_date_range_placeholder')"
                  :apply-label="t('consult_coming_date_range_apply')"
                  :clear-label="t('consult_coming_date_range_clear')"
                  min-width="16rem"
                />
              </div>

              <!-- Created-at range filters disabled for now -->

              <div
                v-if="hasActiveFilters"
                class="mt-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50"
              >
                <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div
                    class="flex shrink-0 items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400"
                  >
                    <span
                      class="flex items-center justify-center w-6 h-6 rounded-lg bg-primary/10 text-primary"
                    >
                      <i class="mdi mdi-filter-variant text-[14px]"></i>
                    </span>
                    <span class="font-medium text-slate-600 dark:text-slate-300">
                      {{ t("consult_filter_active_hint") }}
                    </span>
                    <span
                      v-if="!isLoading && meta.total != null"
                      class="tabular-nums font-semibold text-primary"
                    >
                      ({{ meta.total }})
                    </span>
                  </div>

                  <div class="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                    <button
                      v-for="chip in activeFilterChips"
                      :key="chip.id"
                      type="button"
                      class="group inline-flex items-center gap-1 h-8 max-w-full pl-2.5 pr-1 text-[11px] font-medium rounded-lg border transition-colors border-slate-200/90 bg-white dark:bg-slate-800/80 dark:border-slate-700/80 text-slate-700 dark:text-slate-200 hover:border-primary/35 hover:bg-primary/5 dark:hover:bg-primary/10"
                      @click="removeFilterChip(chip.id)"
                    >
                      <i
                        v-if="chip.icon"
                        class="mdi shrink-0 text-[13px] text-primary/70"
                        :class="chip.icon"
                      ></i>
                      <span class="shrink-0 text-slate-400 dark:text-slate-500">
                        {{ chip.label }}:
                      </span>
                      <span class="truncate max-w-[10rem]">{{ chip.value }}</span>
                      <span
                        class="flex shrink-0 items-center justify-center w-6 h-6 rounded-md text-slate-400 transition-colors group-hover:text-primary group-hover:bg-primary/10"
                      >
                        <i class="mdi mdi-close text-[14px]"></i>
                      </span>
                    </button>
                  </div>

                  <button
                    type="button"
                    class="shrink-0 inline-flex items-center gap-1 h-8 px-2.5 text-[11px] font-semibold rounded-lg text-primary hover:bg-primary/8 transition-colors"
                    @click="clearFilters"
                  >
                    <i class="mdi mdi-filter-off-outline text-[14px]"></i>
                    {{ t("consult_coming_filter_clear_all") }}
                  </button>
                </div>
              </div>
            </header>

            <div class="p-3.5 sm:p-5">
              <div
                v-if="isInitialLoading"
                class="flex flex-col items-center justify-center py-14 text-center"
              >
                <i class="mb-3 text-3xl mdi mdi-loading mdi-spin text-primary/70"></i>
                <p class="text-sm text-slate-500">{{ t("loading") }}</p>
              </div>

              <div
                v-else-if="!consultations.length"
                class="flex flex-col items-center justify-center px-4 py-10 text-center border border-dashed rounded-2xl border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40"
              >
                <i
                  class="mb-3 text-3xl mdi text-secondary"
                  :class="
                    method === 'chat'
                      ? 'mdi-chat-processing-outline'
                      : 'mdi-calendar-clock-outline'
                  "
                ></i>
                <p class="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {{
                    hasActiveFilters
                      ? t("consult_coming_empty_filtered")
                      : t("consult_coming_empty")
                  }}
                </p>
                <p
                  v-if="!hasActiveFilters"
                  class="mt-1 max-w-sm text-xs text-slate-500 dark:text-slate-400"
                >
                  {{ t("consult_coming_empty_hint") }}
                </p>
                <button
                  v-if="hasActiveFilters"
                  type="button"
                  class="inline-flex items-center gap-1.5 px-4 py-2 mt-4 text-xs font-semibold rounded-lg text-primary bg-primary/10 hover:bg-primary/15 transition-colors"
                  @click="clearFilters"
                >
                  <i class="mdi mdi-filter-off-outline text-[16px]"></i>
                  {{ t("consult_coming_filter_clear_all") }}
                </button>
                <RouterLink
                  v-else
                  to="/consultations/my"
                  class="inline-flex items-center gap-1.5 px-4 py-2 mt-4 text-xs font-medium border rounded-lg border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <i class="mdi mdi-clipboard-text-outline text-[16px]"></i>
                  {{ t("consult_coming_back_my") }}
                </RouterLink>
              </div>

              <!-- Consultations Data View (Table on Desktop / Cards on Mobile) -->
              <div
                v-else
                class="rounded-2xl overflow-hidden"
                :class="isRefreshing ? 'opacity-60 pointer-events-none' : ''"
              >
                <!-- 📱 Mobile Card View -->
                <div class="block md:hidden space-y-3">
                  <div
                    v-for="consult in consultations"
                    :key="consult.id"
                    class="p-4 rounded-xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm hover:border-primary/30 transition flex flex-col gap-3"
                  >
                    <!-- Top row: Number Badge & Status -->
                    <div class="flex items-center justify-between gap-2">
                      <ConsultationNumberBadge
                        v-if="consult.consultation_number || consult.number"
                        :consultation-number="consult.consultation_number || consult.number"
                        :status-class="statusBadgeClass(consult.status?.id)"
                        :copy-title="t('consult_copy_number')"
                      />
                      <span v-else class="text-xs text-slate-400">—</span>

                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-lg border"
                        :class="statusBadgeClass(consult.status?.id)"
                      >
                        <i
                          class="mdi text-[13px] shrink-0 opacity-90"
                          :class="consultStatusIconClass(consult.status?.id)"
                        ></i>
                        {{ statusLabel(consult) }}
                      </span>
                    </div>

                    <!-- Subject -->
                    <div>
                      <h3
                        class="text-sm font-semibold text-slate-900 dark:text-white leading-snug line-clamp-2"
                        :title="consult.subject || ''"
                      >
                        {{ consult.subject || "—" }}
                      </h3>
                    </div>

                    <!-- Meta: Type + Appointment Date/Time -->
                    <div class="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-[11px]">
                      <span
                        v-if="consultTypeId(consult)"
                        class="inline-flex items-center gap-1 px-2 py-0.5 font-medium rounded-lg border bg-white dark:bg-slate-900"
                        :class="typeBadgeClass(consultTypeId(consult))"
                      >
                        <i class="mdi mdi-shape-outline text-[13px] shrink-0"></i>
                        <span>{{ typeDisplayLabel(consult) }}</span>
                      </span>

                      <span
                        v-if="appointmentDate(consult)"
                        class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-200"
                      >
                        <i class="mdi mdi-calendar-month-outline text-[13px]"></i>
                        {{ appointmentDate(consult) }}
                      </span>

                      <span
                        v-if="appointmentTime(consult)"
                        class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-200"
                      >
                        <i class="mdi mdi-clock-outline text-[13px]"></i>
                        {{ appointmentTime(consult) }}
                      </span>
                    </div>

                    <!-- Actions Bottom Bar -->
                    <div class="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                      <button
                        type="button"
                        class="flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-semibold rounded-lg bg-primary/10 hover:bg-primary/15 text-primary transition"
                        @click="handleView(consult)"
                      >
                        <i class="mdi mdi-eye-outline text-[16px]"></i>
                        <span>{{ t("consult_action_view") }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- 🖥️ Desktop / Tablet Data Table -->
                <div class="hidden md:block border rounded-2xl border-slate-100 dark:border-slate-800 overflow-hidden">
                  <div class="overflow-x-auto custom-scrollbar">
                    <table class="w-full min-w-[900px] text-sm table-fixed">
                      <colgroup>
                        <col class="w-[18%]" />
                        <col class="w-[20%]" />
                        <col class="w-[18%]" />
                        <col class="w-[19%]" />
                        <col class="w-[13%]" />
                        <col class="w-[12%]" />
                      </colgroup>
                      <thead
                        class="sticky top-0 z-10 text-xs font-medium tracking-wide uppercase text-slate-600 bg-secondary/10 dark:bg-slate-800/90 dark:text-slate-200"
                      >
                        <tr>
                          <th
                            scope="col"
                            class="px-4 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("consult_table_number") }}
                          </th>
                          <th
                            scope="col"
                            class="px-4 py-3"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("consult_table_subject") }}
                          </th>
                          <th
                            scope="col"
                            class="px-4 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("consult_table_type") }}
                          </th>
                          <th
                            scope="col"
                            class="px-4 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("consult_coming_table_appointment") }}
                          </th>
                          <th
                            scope="col"
                            class="px-4 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("consult_table_status") }}
                          </th>
                          <th
                            scope="col"
                            class="px-4 py-3 text-center whitespace-nowrap"
                          >
                            {{ t("consult_table_actions") }}
                          </th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                        <tr
                          v-for="consult in consultations"
                          :key="consult.id"
                          class="transition-colors hover:bg-primary/5 dark:hover:bg-slate-800/60 even:bg-slate-50/60 dark:even:bg-slate-900/40"
                        >
                          <td class="px-4 py-3 align-middle whitespace-nowrap pe-5">
                            <ConsultationNumberBadge
                              v-if="consult.consultation_number || consult.number"
                              :consultation-number="consult.consultation_number || consult.number"
                              :status-class="statusBadgeClass(consult.status?.id)"
                              :copy-title="t('consult_copy_number')"
                            />
                            <span v-else class="text-[11px] text-slate-400">—</span>
                          </td>
                          <td class="px-4 py-3 align-middle min-w-0 overflow-hidden">
                            <span
                              class="block text-sm font-medium truncate text-slate-900 dark:text-slate-50"
                              :title="consult.subject || ''"
                            >
                              {{ consult.subject || "—" }}
                            </span>
                          </td>
                          <td class="px-4 py-3 align-middle min-w-0">
                            <span
                              v-if="consultTypeId(consult)"
                              class="inline-flex items-start gap-1 max-w-full min-w-0 px-2 py-1 text-[11px] font-medium leading-snug rounded-lg border bg-white dark:bg-slate-900"
                              :class="typeBadgeClass(consultTypeId(consult))"
                            >
                              <i class="mdi mdi-shape-outline text-[14px] shrink-0 mt-0.5"></i>
                              <span class="min-w-0 break-words">{{ typeDisplayLabel(consult) }}</span>
                            </span>
                            <span v-else class="text-[11px] text-slate-400 dark:text-slate-500">
                              {{ t("consult_table_type_none") }}
                            </span>
                          </td>
                          <td class="px-4 py-3 align-middle min-w-0 overflow-hidden">
                            <div class="flex flex-wrap items-center gap-1.5">
                              <span
                                v-if="appointmentDate(consult)"
                                class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-200"
                              >
                                <i class="mdi mdi-calendar-month-outline text-[14px]"></i>
                                {{ appointmentDate(consult) }}
                              </span>
                              <span
                                v-if="appointmentTime(consult)"
                                class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-200"
                              >
                                <i class="mdi mdi-clock-outline text-[14px]"></i>
                                {{ appointmentTime(consult) }}
                              </span>
                              <span
                                v-if="!appointmentDate(consult) && !appointmentTime(consult)"
                                class="text-[11px] text-slate-400"
                              >
                                —
                              </span>
                            </div>
                          </td>
                          <td class="px-4 py-3 align-middle whitespace-nowrap">
                            <span
                              class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-lg border"
                              :class="statusBadgeClass(consult.status?.id)"
                            >
                              <i
                                class="mdi text-[13px] shrink-0 opacity-90"
                                :class="consultStatusIconClass(consult.status?.id)"
                              ></i>
                              {{ statusLabel(consult) }}
                            </span>
                          </td>
                          <td class="px-4 py-3 text-center align-middle whitespace-nowrap">
                            <div
                              class="relative inline-flex items-center justify-center"
                              :class="openActionId === consult.id ? 'z-30' : 'z-0'"
                            >
                              <button
                                type="button"
                                class="inline-flex items-center justify-center w-8 h-8 transition bg-white border rounded-full text-slate-500 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:text-primary hover:border-primary/40 hover:bg-primary/5"
                                :aria-label="t('consult_table_actions')"
                                @click.stop="toggleActionMenu(consult.id)"
                              >
                                <i class="mdi mdi-dots-horizontal text-[18px]"></i>
                              </button>

                              <div
                                v-if="openActionId === consult.id"
                                class="absolute end-0 bottom-full mb-1 z-50 w-44 p-1 text-xs bg-white border shadow-lg rounded-xl dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-100"
                                @click.stop
                              >
                                <button
                                  type="button"
                                  class="flex items-center w-full px-3 py-2 text-left transition rounded-lg hover:bg-secondary/10 hover:text-secondary"
                                  @click="handleView(consult)"
                                >
                                  <i
                                    class="mdi mdi-eye-outline text-[16px]"
                                    :class="lang === 'ar' ? 'ml-2' : 'mr-2'"
                                  ></i>
                                  <span>{{ t("consult_action_view") }}</span>
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Pagination -->
                <div
                  class="flex flex-col gap-3 px-4 py-3 bg-white border-t dark:bg-slate-900 border-slate-100 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between mt-3 md:mt-0"
                >
                  <div class="text-xs text-slate-600 dark:text-slate-300">
                    <span v-if="pagination.total">
                      {{ lang === "ar" ? "عرض" : "Showing" }}
                      <b>{{ pagination.from }}</b
                      >–<b>{{ pagination.to }}</b>
                      {{ lang === "ar" ? "من" : "of" }}
                      <b>{{ pagination.total }}</b>
                    </span>
                    <span v-else>—</span>
                  </div>

                  <div
                    class="flex flex-wrap items-center justify-between sm:justify-end gap-3"
                  >
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-slate-500 dark:text-slate-400">
                        {{ lang === "ar" ? "عدد الصفوف" : "Rows" }}
                      </span>
                      <CompactSelect
                        v-model="perPage"
                        :options="perPageSelectOptions"
                        min-width="4.5rem"
                        align="start"
                      />
                    </div>

                    <div class="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        @click="prevPage"
                        :disabled="pagination.current <= 1 || isLoading"
                        class="inline-flex items-center justify-center text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                        aria-label="Previous Page"
                      >
                        <i
                          class="mdi mdi-chevron-left"
                          :class="lang === 'ar' ? 'rotate-180' : ''"
                        ></i>
                      </button>

                      <button
                        v-if="pagesToShow.showFirst"
                        type="button"
                        @click="goToPage(1)"
                        class="text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        1
                      </button>
                      <span
                        v-if="pagesToShow.showLeftDots"
                        class="px-1 text-slate-400"
                        >…</span
                      >

                      <button
                        v-for="p in pagesToShow.pages"
                        :key="p"
                        type="button"
                        @click="goToPage(p)"
                        class="text-xs transition border rounded-lg w-9 h-9"
                        :class="
                          p === pagination.current
                            ? 'bg-primary/10 text-primary border-primary/30'
                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                        "
                      >
                        {{ p }}
                      </button>

                      <span
                        v-if="pagesToShow.showRightDots"
                        class="px-1 text-slate-400"
                        >…</span
                      >
                      <button
                        v-if="pagesToShow.showLast"
                        type="button"
                        @click="goToPage(pagination.last)"
                        class="text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        {{ pagination.last }}
                      </button>

                      <button
                        type="button"
                        @click="nextPage"
                        :disabled="
                          pagination.current >= pagination.last || isLoading
                        "
                        class="inline-flex items-center justify-center text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                        aria-label="Next Page"
                      >
                        <i
                          class="mdi mdi-chevron-right"
                          :class="lang === 'ar' ? 'rotate-180' : ''"
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>

    <Toast
      :show="toast.show"
      :type="toast.type"
      :message="toast.message"
      @update:show="toast.show = $event"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import Toast from "@/components/Toast.vue";
import CompactSelect from "@/components/CompactSelect.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";
import FilterSearchInput from "@/components/FilterSearchInput.vue";
import DateRangePicker from "@/components/DateRangePicker.vue";
import TicketFilterPills from "@/components/tickets/TicketFilterPills.vue";
import ConsultationNumberBadge from "@/components/consultations/ConsultationNumberBadge.vue";
import { consultStatusIconClass } from "@/utils/consultation";
import { consultationsApi } from "@/api/consultations";
import { useFeedStore } from "@/stores/feedStore";
import { usePageI18n } from "@/composables/usePageI18n";
import { useAuthShell } from "@/composables/useAuthShell";
import { navigateToConsultationDetails } from "@/utils/consultationDetailsBootstrap";

const props = defineProps({
  method: {
    type: String,
    required: true,
    validator: (v) => v === "chat" || v === "session",
  },
});

const { lang, t } = usePageI18n();
const {
  isSidebarCollapsed,
  isSidebarOpenMobile,
  sidebarSearch,
  toggleSidebar,
  toast,
  showToast,
} = useAuthShell();

const router = useRouter();
const feedStore = useFeedStore();

const isLoading = ref(true);
const hasLoadedOnce = ref(false);
const consultations = ref([]);
const stats = ref({});
const page = ref(1);
const perPage = ref(10);
const meta = ref({
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
});

const perPageSelectOptions = [
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 30, label: "30" },
  { value: 50, label: "50" },
];

const pagination = computed(() => {
  const m = meta.value;
  const total = Number(m.total ?? 0);
  const current = Number(page.value ?? m.current_page ?? 1);
  const per = Number(perPage.value ?? m.per_page ?? 10);
  const last = Number(m.last_page ?? Math.max(1, Math.ceil(total / per)));
  const from = Number(m.from ?? (total ? (current - 1) * per + 1 : 0));
  const to = Number(m.to ?? Math.min(current * per, total));

  return { total, current, per, last, from, to };
});

const pagesToShow = computed(() => {
  const { current, last } = pagination.value;
  const delta = 2;
  const start = Math.max(1, current - delta);
  const end = Math.min(last, current + delta);

  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return {
    showFirst: start > 1,
    showLeftDots: start > 2,
    pages,
    showRightDots: end < last - 1,
    showLast: end < last,
  };
});

const searchInput = ref("");
const debouncedSearch = ref("");
const activePill = ref("all");
const showAdvanced = ref(false);
let searchTimer = null;

const extraFilters = reactive({
  type_id: "",
  sortBy: "oppointment_date",
  orderBy: "asc",
  appointment_date_from: "",
  appointment_date_to: "",
});

const pageTitle = computed(() =>
  props.method === "chat"
    ? t("consult_coming_chat_title")
    : t("consult_coming_session_title"),
);

const pageSubtitle = computed(() =>
  props.method === "chat"
    ? t("consult_coming_chat_subtitle")
    : t("consult_coming_session_subtitle"),
);

const isInitialLoading = computed(() => isLoading.value && !hasLoadedOnce.value);
const isRefreshing = computed(() => isLoading.value && hasLoadedOnce.value);

const typeOptions = computed(() => {
  const list = feedStore.consultationTypes || [];
  return (Array.isArray(list) ? list : []).map((tp) => {
    const label =
      lang.value === "ar"
        ? tp?.name?.ar || tp?.title?.ar || tp?.name_ar || tp?.title_ar
        : tp?.name?.en || tp?.title?.en || tp?.name_en || tp?.title_en;
    return {
      value: Number(tp.id),
      label: label || String(tp.id),
    };
  });
});

const typeSelectOptions = computed(() => [
  { value: "", label: t("consult_filter_type_all") },
  ...typeOptions.value.map((tp) => ({ value: tp.value, label: tp.label })),
]);

/*
const paymentSelectOptions = computed(() => [
  { value: "", label: t("consult_filter_payment_all") },
  { value: "paid", label: t("payment_status_paid") },
  { value: "unpaid", label: t("payment_status_unpaid") },
  { value: "under_review", label: t("payment_status_under_review") },
  { value: "rejected", label: t("payment_status_rejected") },
  { value: "cancelled", label: t("payment_status_cancelled") },
  { value: "failed", label: t("payment_status_failed") },
]);
*/

const sortSelectOptions = computed(() => [
  { value: "oppointment_date", label: t("consult_coming_sort_appointment_date") },
  { value: "oppointment_time", label: t("consult_coming_sort_appointment_time") },
  // { value: "created_at", label: t("consult_coming_sort_created_at") },
  { value: "price", label: t("consult_coming_sort_price") },
  { value: "consultation_number", label: t("consult_coming_sort_number") },
  // { value: "status_id", label: t("consult_coming_sort_status") },
]);

const orderSelectOptions = computed(() => [
  { value: "asc", label: t("consult_coming_order_asc") },
  { value: "desc", label: t("consult_coming_order_desc") },
]);

function pickStatCount(source, ...keys) {
  if (!source || typeof source !== "object") return null;

  for (const key of keys) {
    const value = source[key];
    if (value != null && value !== "") return Number(value);
  }

  const nested =
    source.payment_status ??
    source.payment_statuses ??
    source.payments ??
    source.by_payment_status;

  if (nested && typeof nested === "object" && nested.paid != null) {
    return Number(nested.paid);
  }

  return null;
}

function normalizeStats(raw = {}) {
  return {
    ...raw,
    total: pickStatCount(raw, "total"),
    today: pickStatCount(raw, "today"),
    this_week: pickStatCount(raw, "this_week"),
    accepted: pickStatCount(raw, "accepted"),
    under_study: pickStatCount(raw, "under_study", "under_review"),
    paid: pickStatCount(
      raw,
      "paid",
      "payment_paid",
      "paid_count",
      "payments_paid",
    ),
  };
}

const filterPills = computed(() => [
  {
    key: "all",
    label: t("consult_coming_filter_all"),
    count: stats.value.total ?? null,
    icon: "mdi-view-grid-outline",
  },
  {
    key: "today",
    label: t("consult_coming_filter_today"),
    count: stats.value.today ?? null,
    icon: "mdi-calendar-today-outline",
  },
  {
    key: "this_week",
    label: t("consult_coming_filter_this_week"),
    count: stats.value.this_week ?? null,
    icon: "mdi-calendar-week-outline",
  },
  {
    key: "accepted",
    label: t("consult_status_accepted"),
    count: stats.value.accepted ?? null,
    icon: "mdi-check-circle-outline",
  },
  {
    key: "under_study",
    label: t("consult_coming_filter_under_study"),
    count: stats.value.under_study ?? null,
    icon: "mdi-progress-clock",
  },
  {
    key: "paid",
    label: t("payment_status_paid"),
    count: stats.value.paid ?? 0,
    icon: "mdi-credit-card-check-outline",
  },
]);

const hasActiveFilters = computed(() => {
  return (
    activePill.value !== "all" ||
    debouncedSearch.value ||
    extraFilters.type_id ||
    extraFilters.appointment_date_from ||
    extraFilters.appointment_date_to ||
    extraFilters.sortBy !== "oppointment_date" ||
    extraFilters.orderBy !== "asc"
  );
});

const activeFilterChips = computed(() => {
  const chips = [];

  if (activePill.value !== "all") {
    const pill = filterPills.value.find((p) => p.key === activePill.value);
    chips.push({
      id: "pill",
      label: t("consult_coming_filter_status"),
      value: pill?.label || activePill.value,
      icon: pill?.icon || "mdi-label-outline",
    });
  }

  if (debouncedSearch.value) {
    chips.push({
      id: "search",
      label: t("consult_coming_filter_search"),
      value: debouncedSearch.value,
      icon: "mdi-magnify",
    });
  }

  if (extraFilters.type_id) {
    const type = typeSelectOptions.value.find(
      (o) => String(o.value) === String(extraFilters.type_id),
    );
    chips.push({
      id: "type_id",
      label: t("consult_coming_filter_type"),
      value: type?.label || String(extraFilters.type_id),
      icon: "mdi-shape-outline",
    });
  }

  if (extraFilters.appointment_date_from || extraFilters.appointment_date_to) {
    const from = extraFilters.appointment_date_from || "…";
    const to = extraFilters.appointment_date_to || "…";
    chips.push({
      id: "appointment_range",
      label: t("consult_coming_filter_appointment_range"),
      value: `${from} – ${to}`,
      icon: "mdi-calendar-range",
    });
  }

  /*
  if (extraFilters.payment_status) {
    const payment = paymentSelectOptions.value.find(
      (o) => o.value === extraFilters.payment_status,
    );
    chips.push({
      id: "payment_status",
      label: t("consult_coming_filter_payment"),
      value: payment?.label || extraFilters.payment_status,
      icon: "mdi-credit-card-outline",
    });
  }
  */

  if (extraFilters.sortBy !== "oppointment_date" || extraFilters.orderBy !== "asc") {
    const sort = sortSelectOptions.value.find((o) => o.value === extraFilters.sortBy);
    const order = orderSelectOptions.value.find((o) => o.value === extraFilters.orderBy);
    chips.push({
      id: "sort",
      label: t("consult_coming_filter_sort"),
      value: `${sort?.label || extraFilters.sortBy} (${order?.label || extraFilters.orderBy})`,
      icon: "mdi-sort",
    });
  }

  return chips;
});

watch(searchInput, (val) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    debouncedSearch.value = val.trim();
    page.value = 1;
    loadConsultations();
  }, 300);
});

watch(
  () => [
    extraFilters.type_id,
    extraFilters.sortBy,
    extraFilters.orderBy,
    extraFilters.appointment_date_from,
    extraFilters.appointment_date_to,
  ],
  () => {
    page.value = 1;
    loadConsultations();
  },
);

watch(perPage, () => {
  page.value = 1;
  loadConsultations();
});

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function weekRangeIso() {
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    from: monday.toISOString().slice(0, 10),
    to: sunday.toISOString().slice(0, 10),
  };
}

function buildFilterParams() {
  const params = {
    page: page.value,
    itemsPerPage: perPage.value,
    sortBy: extraFilters.sortBy,
    orderBy: extraFilters.orderBy,
  };

  if (debouncedSearch.value) params.search = debouncedSearch.value;
  if (extraFilters.type_id) params.type_id = Number(extraFilters.type_id);

  const pill = activePill.value;
  if (pill === "today") {
    params.appointment_date_from = todayIso();
    params.appointment_date_to = todayIso();
  } else if (pill === "this_week") {
    const week = weekRangeIso();
    params.appointment_date_from = week.from;
    params.appointment_date_to = week.to;
  } else if (pill === "accepted") {
    params.status = 4;
  } else if (pill === "under_study") {
    params.status = 3;
  } else if (pill === "paid") {
    params.payment_status = "paid";
  }

  if (extraFilters.appointment_date_from) {
    params.appointment_date_from = extraFilters.appointment_date_from;
  }
  if (extraFilters.appointment_date_to) {
    params.appointment_date_to = extraFilters.appointment_date_to;
  }

  return params;
}

async function loadConsultations() {
  isLoading.value = true;
  try {
    const fetcher =
      props.method === "chat"
        ? consultationsApi.comingChat
        : consultationsApi.comingSession;
    const res = await fetcher(buildFilterParams());
    consultations.value = res.data || [];
    stats.value = normalizeStats(res.stats || {});
    const total = res.meta?.total ?? consultations.value.length;
    meta.value = {
      current_page: res.meta?.current_page ?? page.value,
      last_page: res.meta?.last_page ?? 1,
      per_page: res.meta?.per_page ?? perPage.value,
      total,
      from: res.meta?.from,
      to: res.meta?.to,
    };
    page.value = Number(meta.value.current_page);
    perPage.value = Number(meta.value.per_page);
  } catch (e) {
    console.error(e);
    consultations.value = [];
    showToast(t("consult_coming_load_failed"), "error");
  } finally {
    isLoading.value = false;
    hasLoadedOnce.value = true;
  }
}

function onPillChange(key) {
  activePill.value = key;
  page.value = 1;
  loadConsultations();
}

function clearSearch() {
  searchInput.value = "";
  debouncedSearch.value = "";
  page.value = 1;
  loadConsultations();
}

function resetExtraFilters() {
  extraFilters.type_id = "";
  extraFilters.sortBy = "oppointment_date";
  extraFilters.orderBy = "asc";
  extraFilters.appointment_date_from = "";
  extraFilters.appointment_date_to = "";
}

function clearFilters() {
  activePill.value = "all";
  if (searchInput.value) {
    searchInput.value = "";
    debouncedSearch.value = "";
  }
  resetExtraFilters();
  showAdvanced.value = false;
  page.value = 1;
  loadConsultations();
}

function removeFilterChip(id) {
  if (id === "pill") {
    activePill.value = "all";
  } else if (id === "search") {
    clearSearch();
    return;
  } else if (id === "sort") {
    extraFilters.sortBy = "oppointment_date";
    extraFilters.orderBy = "asc";
  } else if (id === "appointment_range") {
    extraFilters.appointment_date_from = "";
    extraFilters.appointment_date_to = "";
  } else if (Object.prototype.hasOwnProperty.call(extraFilters, id)) {
    extraFilters[id] = "";
  }
  page.value = 1;
  loadConsultations();
}

function goToPage(p) {
  const n = Number(p);
  if (!n) return;
  if (n < 1 || n > pagination.value.last) return;
  page.value = n;
  loadConsultations();
}

function prevPage() {
  if (pagination.value.current > 1) goToPage(pagination.value.current - 1);
}

function nextPage() {
  if (pagination.value.current < pagination.value.last) {
    goToPage(pagination.value.current + 1);
  }
}

function findType(typeId) {
  const id = Number(typeId);
  return typeOptions.value.find((tp) => tp.value === id);
}

function findTypeFromStore(typeId) {
  const id = Number(typeId);
  const list = feedStore.consultationTypes || [];
  return (Array.isArray(list) ? list : []).find((x) => Number(x.id) === id);
}

function consultTypeId(consult) {
  const raw = consult.type_id ?? consult.type?.id ?? consult.type;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string" && /^[0-9]+$/.test(raw)) return Number(raw);
  return null;
}

function typeLabelKey(typeId) {
  const tp = findTypeFromStore(typeId);
  if (!tp) return "consult_table_type_none";

  const label =
    lang.value === "ar"
      ? tp?.name?.ar || tp?.title?.ar || tp?.name_ar || tp?.title_ar
      : tp?.name?.en || tp?.title?.en || tp?.name_en || tp?.title_en;

  return label || tp?.key || tp?.slug || String(tp.id);
}

function typeDisplayLabel(consult) {
  const typeId = consultTypeId(consult);
  if (typeId) return t(typeLabelKey(typeId));
  return (
    consult.type?.title?.[lang.value] ||
    consult.type?.name ||
    t("consult_table_type_none")
  );
}

function typeKeyFromStore(typeRef) {
  const typeId =
    typeof typeRef === "object"
      ? typeRef?.id ?? typeRef?.key ?? typeRef?.name
      : typeRef;
  const tp = findTypeFromStore(typeId);
  const raw = tp?.key || tp?.slug || tp?.name_en || tp?.title_en || "";
  return String(raw || "").toLowerCase().trim();
}

function typeBadgeClass(typeRef) {
  const key = typeKeyFromStore(typeRef);
  if (key.includes("government")) {
    return "border-rose-300 text-rose-700 dark:border-rose-700 dark:text-rose-300 bg-rose-50/60 dark:bg-rose-950/40";
  }
  if (key.includes("marketing")) {
    return "border-secondary/50 text-secondary bg-secondary/5 dark:bg-secondary/20";
  }
  return "border-primary/40 text-primary bg-primary/5";
}

const openActionId = ref(null);

function toggleActionMenu(id) {
  openActionId.value = openActionId.value === id ? null : id;
}

function handleClickOutside() {
  if (openActionId.value !== null) openActionId.value = null;
}

function appointmentDate(consult) {
  return consult.oppointment_date || consult.appointment_date || "";
}

function appointmentTime(consult) {
  return consult.oppointment_time || consult.appointment_time || "";
}

function statusLabel(consult) {
  return (
    consult.status?.title?.[lang.value] ||
    consult.status?.name ||
    consult.status_name ||
    "—"
  );
}

function statusBadgeClass(statusId) {
  const status = Number(statusId);
  if (status === 1)
    return "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
  if (status === 2)
    return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800";
  if (status === 3)
    return "bg-secondary/10 text-secondary border border-secondary/50";
  if (status === 4)
    return "bg-primary/10 text-primary border border-primary/50";
  if (status === 5)
    return "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
  return "bg-slate-100 text-slate-600 border border-slate-300";
}

function paymentStatusLabelKey(value) {
  const s = String(value || "").toLowerCase();
  if (s === "paid") return "payment_status_paid";
  if (s === "unpaid") return "payment_status_unpaid";
  if (["under_review", "underreview", "review"].includes(s)) {
    return "payment_status_under_review";
  }
  if (s === "rejected") return "payment_status_rejected";
  if (s === "cancelled") return "payment_status_cancelled";
  if (s === "failed") return "payment_status_failed";
  return "payment_status_unknown";
}

function paymentBadgeClass(value) {
  const s = String(value || "").toLowerCase();
  if (s === "paid") {
    return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800";
  }
  if (["under_review", "underreview", "review"].includes(s)) {
    return "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800";
  }
  if (s === "unpaid") {
    return "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
  }
  if (s === "rejected" || s === "failed" || s === "cancelled") {
    return "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800";
  }
  return "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
}

function handleView(consult) {
  openActionId.value = null;
  navigateToConsultationDetails(router, consult);
}

function primaryActionVisible(consult) {
  if (props.method === "chat") return true;
  return Boolean(consult.oppointment_url || consult.appointment_url);
}

function primaryActionTitle(consult) {
  if (props.method === "chat") {
    return lang.value === "ar" ? "فتح المحادثة" : "Open chat";
  }
  return lang.value === "ar" ? "دخول الجلسة" : "Join session";
}

function handlePrimaryAction(consult) {
  openActionId.value = null;
  if (props.method === "chat") {
    router.push({ name: "ConsultationChat", params: { id: consult.id } });
    return;
  }
  const url = consult.oppointment_url || consult.appointment_url;
  if (url) window.open(url, "_blank", "noopener,noreferrer");
}

onMounted(async () => {
  document.addEventListener("click", handleClickOutside);
  await feedStore.fetchFeedOnce();
  loadConsultations();
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>
